import { useEffect, useState } from 'react';
import { useJsonApi } from './JsonApi';
import { useGlobalStore } from 'stores';
import { NavEntity } from 'types/header';
import { absoluteUrl } from 'utils';

export interface DrupalMenuItem {
  id: string;
  menu_name: string;
  type: string;
  enabled: boolean;
  expanded: boolean;
  title: string;
  description: string;
  url: string;
  meta: Record<string, unknown>;
  options: Record<string, unknown>;
  parent: string;
  provider: string;
  weight: number;
  view_mode: string;
  route: {
    name: string;
    parameters: Record<string, unknown>;
  };
  field_reference?: {
    title?: string;
    field_description?: string;
    field_external_link?: {
      uri?: string;
    };
    field_background_image?: {
      uri?: {
        url?: string;
      };
    };
  };
}

export interface MenuItem extends DrupalMenuItem {
  items: MenuItem[];
}

function buildTree(items: DrupalMenuItem[]): MenuItem[] {
  const map: Record<string, MenuItem> = {};
  const roots: MenuItem[] = [];

  items.forEach(item => {
    map[item.id] = {
      ...item,
      items: [],
    };
  });

  items.forEach(item => {
    const parentId = item.parent;

    if (parentId && map[parentId]) {
      map[parentId].items.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });

  const sortByWeight = (nodes: MenuItem[]) => {
    nodes.sort((a, b) => a.weight - b.weight);
    nodes.forEach(node => sortByWeight(node.items));
  };

  sortByWeight(roots);

  return roots;
}

export function useLoadMenu(): NavEntity[] {
  const { showLoader, hideLoader } = useGlobalStore();
  const { getMenu } = useJsonApi({ showLoader, hideLoader });

  const [menuTree, setMenuTree] = useState<NavEntity[]>([]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await getMenu({
          useLoader: true,
        });

        const data = res as MenuItem[] | null;
        let builtTree: MenuItem[] | null = null;
        if (data) {
          builtTree = buildTree(data);
        }
        if (!builtTree) throw new Error('API menu not ok');

        const parsedMenu: NavEntity[] = builtTree?.map(item => {
          return {
            id: item.id,
            title: item.title,
            path: item.url,
            hasDropdown: (item.items?.length ?? 0) > 0,
            disabled: !item.enabled,
            dropdownItems: item.items?.map(item => ({
              id: item.id,
              title: item.title,
              path: item.url,
              disabled: !item.enabled,
              children: item.items?.map(item => ({
                id: item.id,
                title: item.title,
                path: item.url,
                disabled: !item.enabled,
              })),
            })),
            ...(item.field_reference && {
              cardEvent: {
                title: item.field_reference.title || '',
                description:
                  item.field_reference.field_description ||
                  'Scopri come aderire',
                path: item.field_reference.field_external_link?.uri || '',
                image: absoluteUrl(
                  item.field_reference.field_background_image?.uri?.url || ''
                ),
              },
            }),
          };
        });
        // console.log('🚀 ~ load ~ parsedMenu:', parsedMenu);

        if (!mounted) return;

        setMenuTree(parsedMenu);
      } catch (err) {
        console.warn('Failed to fetch menu from API', err);
        if (mounted) setMenuTree([]);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [getMenu]);

  return menuTree;
}
